export interface FileValidationParams {
  fileName: string;
  fileSize: number;
  mimeType: string;
}

export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

// File size limits (in bytes)
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const MIN_FILE_SIZE = 1; // 1 byte

// Allowed MIME types
const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "image/avif",
] as const;

const ALLOWED_VIDEO_TYPES = [
  "video/mp4",
  "video/webm",
  "video/ogg",
  "video/quicktime",
  "video/x-msvideo", // .avi
] as const;

const ALLOWED_MIME_TYPES = [...ALLOWED_IMAGE_TYPES, ...ALLOWED_VIDEO_TYPES] as const;

// Allowed file extensions
const ALLOWED_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg", ".avif"] as const;
const ALLOWED_VIDEO_EXTENSIONS = [".mp4", ".webm", ".ogg", ".mov", ".avi"] as const;
const ALLOWED_EXTENSIONS = [...ALLOWED_IMAGE_EXTENSIONS, ...ALLOWED_VIDEO_EXTENSIONS] as const;

/**
 * Extract file extension from filename
 */
export function getFileExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf(".");
  if (lastDot === -1 || lastDot === fileName.length - 1) {
    return "";
  }
  return fileName.substring(lastDot).toLowerCase();
}

/**
 * Validate file parameters according to platform requirements
 */
export function validateFile(params: FileValidationParams): FileValidationResult {
  const { fileName, fileSize, mimeType } = params;

  // Check if filename is provided
  if (!fileName || fileName.trim().length === 0) {
    return {
      isValid: false,
      error: "File name is required",
    };
  }

  // Check file size limits
  if (fileSize < MIN_FILE_SIZE) {
    return {
      isValid: false,
      error: "File is empty",
    };
  }

  if (fileSize > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `File size must be less than ${Math.round(MAX_FILE_SIZE / 1024 / 1024)}MB`,
    };
  }

  // Check MIME type
  if (!mimeType || !(ALLOWED_MIME_TYPES as readonly string[]).includes(mimeType)) {
    return {
      isValid: false,
      error: "File type not supported. Please upload an image or video file.",
    };
  }

  // Check file extension
  const extension = getFileExtension(fileName);
  if (!extension || !(ALLOWED_EXTENSIONS as readonly string[]).includes(extension)) {
    return {
      isValid: false,
      error: "File extension not supported. Allowed: " + ALLOWED_EXTENSIONS.join(", "),
    };
  }

  // Additional security checks
  if (fileName.includes("..") || fileName.includes("/") || fileName.includes("\\")) {
    return {
      isValid: false,
      error: "Invalid characters in filename",
    };
  }

  // Check for potentially dangerous filenames
  const dangerousPatterns = [
    /^\./, // Files starting with dot
    /\.(exe|bat|cmd|com|pif|scr|vbs|js)$/i, // Executable extensions
    /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(\.|$)/i, // Windows reserved names
  ];

  for (const pattern of dangerousPatterns) {
    if (pattern.test(fileName)) {
      return {
        isValid: false,
        error: "Filename contains invalid patterns",
      };
    }
  }

  return {
    isValid: true,
  };
}

/**
 * Check if a MIME type is an image
 */
export function isImageMimeType(mimeType: string): boolean {
  return (ALLOWED_IMAGE_TYPES as readonly string[]).includes(mimeType);
}

/**
 * Check if a MIME type is a video
 */
export function isVideoMimeType(mimeType: string): boolean {
  return (ALLOWED_VIDEO_TYPES as readonly string[]).includes(mimeType);
}

/**
 * Get human-readable file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}
